import {
    defineEntityItemDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import {
    buildPanelShape,
} from '@toolproof-core/visualization/_pure/shapes';
import type { EntityItemCollectionDerivation } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { digitAtPower } from '@/adapters/primitive/_pure/model/natural/columnArithmetic';
import {
    toBooleanDiscItemKey,
    toNaturalDigitItemKey,
    toPrimitivePowerCollectionKey,
} from '@/adapters/primitive/_pure/derivation/keyCodec';
import { isPrimitiveDiscWheel, toPrimitiveBandOpacity } from '@/adapters/primitive/_pure/derivation/wheels';
import type { Config, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';

/** Digits high-to-low with the zero slot last, so index === 9 - digit and the seam sits between 1 and 9. */
export function toNaturalRenderedDigits(config: Config): number[] {
    return [...[...config.natural.digits].reverse(), 0];
}

const baseDisplay = (config: Config) => config.base.display;

/** The rendered opacity of a band's position-0 slot, including any band-wide dimming. */
export function toNaturalZeroSlotOpacity(
    config: Config,
    wheel: PrimitiveWheelState,
    power: number,
): number {
    return config.natural.display.cell.zeroSlotOpacity
        * toPrimitiveBandOpacity(wheel, power);
}

export function toNaturalCellOpacity(args: {
    config: Config;
    wheel: PrimitiveWheelState;
    power: number;
    digit: number;
}): number {
    const { config, wheel, power, digit } = args;
    if (isPrimitiveDiscWheel(wheel)) {
        throw new Error('Disc wheels do not have Natural digit cells.');
    }
    const explicitCounted = wheel.countedDigitsByPower?.[power];
    if (digit === 0 && wheel.markByPower?.[power] === 'deficit') {
        return config.base.display.cell.countedOpacity * toPrimitiveBandOpacity(wheel, power);
    }
    if (digit === 0 && !explicitCounted?.includes(0)) {
        return toNaturalZeroSlotOpacity(config, wheel, power);
    }

    const countedThroughDigit = wheel.countedThroughDigitByPower?.[power]
        ?? digitAtPower({ value: wheel.value, power });
    const isCounted = explicitCounted !== undefined ? explicitCounted.includes(digit) : digit <= countedThroughDigit;
    const uncountedCellOpacity = wheel.variant === 'flight' || wheel.variant === 'radix-band'
        ? baseDisplay(config).cell.flightUncountedOpacity
        : baseDisplay(config).cell.uncountedOpacity;

    return (isCounted ? baseDisplay(config).cell.countedOpacity : uncountedCellOpacity)
        * toPrimitiveBandOpacity(wheel, power);
}

export function buildNaturalDigitItemCollectionDerivations(
    config: Config,
    wheel: PrimitiveWheelState,
): EntityItemCollectionDerivation[] {
    if (wheel.valueKind === 'boolean'
        || wheel.valueKind === 'comparison-helper'
        || wheel.valueKind === 'placeholder') {
        throw new Error('Disc wheels do not have Natural digit collections.');
    }
    return wheel.powers.map((power) => {
        const explicitCounted = wheel.countedDigitsByPower?.[power];
        const countedThroughDigit = wheel.countedThroughDigitByPower?.[power] ?? digitAtPower({ value: wheel.value, power });
        const overhangDigits = wheel.overhangDigitsByPower?.[power] ?? [];
        const bandOpacity = toPrimitiveBandOpacity(wheel, power);
        const marker = wheel.markByPower?.[power];
        const overrides = wheel.cellOverridesByPower?.[power] ?? {};
        const itemDerivations = toNaturalRenderedDigits(config).map((digit) => {
            const isZeroSlot = digit === 0;
            const isOverhang = overhangDigits.includes(digit);
            const isInCountedRange = explicitCounted !== undefined
                ? explicitCounted.includes(digit)
                : !isZeroSlot && digit <= countedThroughDigit;
            // A crossed cell is overhang, not counted: its contribution is accounted for at the next power.
            const isCounted = isInCountedRange && !isOverhang;
            const isDecisive = !isCounted
                && wheel.comparisonDecisiveCell?.power === power
                && wheel.comparisonDecisiveCell.digit === digit;
            const isMarkedZero = isZeroSlot && marker === 'deficit';
            const cellOpacity = (isOverhang || isDecisive
                ? config.base.display.cell.countedOpacity * bandOpacity
                : toNaturalCellOpacity({ config, wheel, power, digit }))
                * (overrides[digit]?.opacity ?? 1);

            return defineEntityItemDerivation({
                item: {
                    itemKind: 'entity',
                    itemKey: toNaturalDigitItemKey({ wheelRole: wheel.role, variant: wheel.variant, power, digit }),
                    name: isZeroSlot
                        ? `${wheel.role} 10^${power} position 0`
                        : `${wheel.role} 10^${power} digit ${digit}`,
                    description: isZeroSlot
                        ? `Position 0 at the ${wheel.role} 10^${power} level.`
                        : `Digit ${digit} slot at the ${wheel.role} 10^${power} level.`,
                },
                parametricState: {
                    // Always present so settlement resolution can tween every cell from rest.
                    layoutNormalOffset: overrides[digit]?.offset ?? 0,
                    ...(config.natural.display.cell.relief.enabled
                        && !(isCounted || isOverhang || isDecisive || isMarkedZero)
                        ? { thicknessScale: config.natural.display.cell.relief.emptyThicknessScale }
                        : {}),
                    shape: buildPanelShape({ width: 1, height: 1 }),
                    visualStyle: {
                        color: isMarkedZero
                            ? config.natural.display.cell.deficitMarkerColor
                            : isOverhang
                                    ? config.natural.display.cell.overhangColor
                                    : isDecisive
                                        ? config.natural.display.cell.comparisonDecisiveCellColor
                                    : isCounted
                                        ? config.natural.display.cell.relief.enabled
                                            // Relief alone conveys countedness while it is being judged.
                                            ? config.base.display.cell.uncountedColor
                                            : config.base.display.cell.countedColor
                                        : isZeroSlot
                                            ? config.natural.display.cell.zeroSlotColor
                                            : config.base.display.cell.uncountedColor,
                        opacity: cellOpacity,
                    },
                    label: config.natural.display.label.digitLabelsVisible && isCounted
                        ? {
                            onItem: {
                                text: String(digit),
                                color: config.base.display.label.digitLabelColor,
                                orientation: {
                                    orientationKind: 'fixed',
                                    plane: 'upright',
                                    facing: 'inward',
                                },
                                doubleSidedReadable: true,
                            },
                        }
                        : {},
                },
            });
        });

        return toEntityItemCollectionDerivation({
            itemCollectionKey: toPrimitivePowerCollectionKey({ wheelRole: wheel.role, variant: wheel.variant, power }),
            itemDerivations,
        });
    });
}

export function buildBooleanDiscItemCollectionDerivations(
    config: Config,
    wheel: PrimitiveWheelState,
): EntityItemCollectionDerivation[] {
    if (!isPrimitiveDiscWheel(wheel)) {
        throw new Error('Natural wheels do not have disc collections.');
    }
    const power = 0;
    return [toEntityItemCollectionDerivation({
        itemCollectionKey: toPrimitivePowerCollectionKey({ wheelRole: wheel.role, variant: wheel.variant, power }),
        itemDerivations: [defineEntityItemDerivation({
            item: {
                itemKind: 'entity',
                itemKey: toBooleanDiscItemKey({ wheelRole: wheel.role, variant: wheel.variant, power }),
                name: wheel.valueKind === 'boolean'
                    ? `${wheel.role} Boolean ${wheel.value}`
                    : wheel.valueKind === 'placeholder'
                        ? `${wheel.role} ${wheel.resourceKind === 'boolean' ? 'Boolean' : 'Natural'} placeholder`
                    : `${wheel.role} unresolved comparison helper`,
                description: wheel.valueKind === 'boolean'
                    ? `Unsegmented Boolean ${wheel.value} wheel.`
                    : wheel.valueKind === 'placeholder'
                        ? 'Hub ring standing in for an unmaterialized output wheel.'
                    : 'Neutral unsegmented disc awaiting a comparison decision.',
            },
            parametricState: {
                ...(wheel.valueKind === 'placeholder' || wheel.valueKind === 'comparison-helper'
                    ? {
                        shapePresentation: {
                            appearanceMode: 'outlined' as const,
                            border: {
                                color: config.base.display.placeholder.outlineColor,
                                opacity: config.base.display.placeholder.outlineOpacity,
                                width: config.base.display.placeholder.outlineWidth,
                            },
                        },
                    }
                    : wheel.role === 'less-than-decision'
                        ? { shapePresentation: {
                            appearanceMode: 'filled' as const,
                            border: {
                                color: config.base.display.placeholder.outlineColor,
                                opacity: 0,
                                width: config.base.display.placeholder.outlineWidth,
                            },
                        } }
                        : {}),
                visualStyle: {
                    color: wheel.valueKind === 'placeholder' || wheel.valueKind === 'comparison-helper'
                            ? config.base.display.placeholder.outlineColor
                        : wheel.value
                            ? config.base.display.cell.countedColor
                            : config.base.display.cell.uncountedColor,
                    opacity: (wheel.valueKind === 'placeholder' || wheel.valueKind === 'comparison-helper'
                            ? 1
                        : wheel.value
                            ? config.base.display.cell.countedOpacity
                            : config.base.display.cell.uncountedOpacity)
                        * toPrimitiveBandOpacity(wheel, power),
                },
            },
        })],
    })];
}